export interface PublicCartItem {
  id: string;
  eventId: string;
  ticketTypeId: string;
  sessionIds: string[];
  quantity: number;
  addedAt: string;
}

const CART_KEY = 'clubsbooking.publicCart.v1';

export const getPublicCart = (): PublicCartItem[] => {
  try {
    const raw = window.localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        id: String(item?.id || '').trim(),
        eventId: String(item?.eventId || '').trim(),
        ticketTypeId: String(item?.ticketTypeId || '').trim(),
        sessionIds: Array.isArray(item?.sessionIds) ? item.sessionIds.map((id: unknown) => String(id).trim()).filter(Boolean) : [],
        quantity: 1,
        addedAt: String(item?.addedAt || new Date().toISOString()),
      }))
      .filter((item) => item.id && item.eventId && item.ticketTypeId && item.sessionIds.length > 0);
  } catch {
    return [];
  }
};

export const savePublicCart = (items: PublicCartItem[]) => {
  window.localStorage.setItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('clubsbooking-cart-updated'));
};

export const addPublicCartItem = (item: PublicCartItem): PublicCartItem[] => {
  const signature = `${item.eventId}::${item.ticketTypeId}::${[...item.sessionIds].sort().join('|')}`;
  const current = getPublicCart();
  const alreadyExists = current.some((existing) => {
    const existingSignature = `${existing.eventId}::${existing.ticketTypeId}::${[...existing.sessionIds].sort().join('|')}`;
    return existingSignature === signature;
  });
  const next = [...current];
  if (!alreadyExists) {
    next.push({
      ...item,
      quantity: 1,
    });
  }
  savePublicCart(next);
  return next;
};

export const getPublicCartItemsForEvent = (eventId: string): PublicCartItem[] => {
  return getPublicCart().filter((item) => item.eventId === eventId);
};

export const updatePublicCartItemQuantity = (id: string, quantity: number): PublicCartItem[] => {
  const next = getPublicCart()
    .map((item) => (item.id === id ? { ...item, quantity: 1 } : item))
    .filter((item) => item.quantity > 0);
  savePublicCart(next);
  return next;
};

export const removePublicCartItem = (id: string): PublicCartItem[] => {
  const next = getPublicCart().filter((item) => item.id !== id);
  savePublicCart(next);
  return next;
};

export const getPublicCartTotalQuantity = (): number => {
  return getPublicCart().reduce((sum, item) => sum + Math.max(1, item.quantity || 1), 0);
};

export const removePublicCartItemForEvent = (eventId: string): PublicCartItem[] => {
  const next = getPublicCart().filter((item) => item.eventId !== eventId);
  savePublicCart(next);
  return next;
};
